/**
 * Dialog "Modifica famiglia" per supplier_product_families.
 *
 * Regole edit:
 *   - **Nome**: sempre modificabile (è solo una label, no referenze esterne).
 *   - **Codice**: sempre modificabile (sigla human-readable, materiali usano `family_id` UUID).
 *   - **Brand** / **Descrizione**: sempre modificabili (pure metadata).
 *   - **Categoria**: modificabile SOLO se la famiglia ha 0 materiali. Cambiarla
 *     mentre ci sono materiali assegnati può rompere il classifier nel
 *     configuratore (un layer "lastra" verrebbe interpretato come "profilo" se
 *     la categoria sottostante cambia). In quel caso il campo è disabled +
 *     hint esplicativo che invita prima a spostare i materiali altrove.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, Info } from 'lucide-react';
import { useUpdateFamily, type FamilyRow } from '@/hooks/useSupplierManagement';

interface EditFamilyDialogProps {
  family: FamilyRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditFamilyDialog: React.FC<EditFamilyDialogProps> = ({ family, open, onOpenChange }) => {
  const update = useUpdateFamily();

  const [code, setCode] = useState(family.code);
  const [name, setName] = useState(family.name);
  const [category, setCategory] = useState(family.category ?? '');
  const [brand, setBrand] = useState(family.brand ?? '');
  const [description, setDescription] = useState(family.description ?? '');

  // Reset campi all'apertura (o al cambio family)
  useEffect(() => {
    if (open) {
      setCode(family.code);
      setName(family.name);
      setCategory(family.category ?? '');
      setBrand(family.brand ?? '');
      setDescription(family.description ?? '');
    }
  }, [open, family]);

  // Categoria modificabile solo se 0 materiali (vedi commento in cima al file)
  const categoryLocked = family.materials_count > 0;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return;
    update.mutate(
      {
        id: family.id,
        supplier_id: family.supplier_id,
        code,
        name,
        // Se la categoria è locked, riusa il valore originale per evitare update accidentale
        category: categoryLocked ? family.category : (category || null),
        brand: brand || null,
        description: description || null,
      },
      {
        onSuccess: () => onOpenChange(false),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifica famiglia</DialogTitle>
          <DialogDescription>
            Aggiorna i dati della famiglia "<strong>{family.name}</strong>".
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ef-code">Codice*</Label>
              <Input
                id="ef-code"
                required
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder='es. "HABITO", "FIRELINE"'
              />
              <p className="text-[10px] text-muted-foreground mt-1">
                Sigla human-readable. Cambiandola non rompe nulla (i materiali sono linkati via UUID).
              </p>
            </div>
            <div>
              <Label htmlFor="ef-name">Nome*</Label>
              <Input
                id="ef-name"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="es. Lastre Habito alta resistenza"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ef-cat" className="flex items-center gap-1">
                Categoria
                {categoryLocked && <Lock className="h-3 w-3 text-muted-foreground" />}
              </Label>
              <Input
                id="ef-cat"
                value={categoryLocked ? (family.category ?? '') : category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder="es. Lastre, Profili, Isolanti"
                disabled={categoryLocked}
              />
              {categoryLocked && (
                <p className="text-[10px] text-amber-700 mt-1 flex items-start gap-1">
                  <Info className="h-3 w-3 mt-0.5 flex-shrink-0" />
                  <span>
                    Bloccata: <strong>{family.materials_count} materiali</strong> assegnati.
                    Cambiarla rischierebbe di confondere il classifier delle stratigrafie. Sposta
                    prima i materiali in un'altra famiglia o crea una nuova famiglia.
                  </span>
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="ef-brand">Brand</Label>
              <Input
                id="ef-brand"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="es. Gyproc, Knauf"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ef-desc">Descrizione</Label>
            <Textarea
              id="ef-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="Note descrittive (opzionale)"
            />
          </div>

          {family.materials_count > 0 && (
            <Alert>
              <AlertDescription className="text-xs">
                Questa famiglia ha <strong>{family.materials_count} materiali</strong> collegati.
                Modifiche a <strong>nome</strong> e <strong>codice</strong> si propagano
                automaticamente a tutti i materiali (sono link via ID, non testo).
              </AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
              Annulla
            </Button>
            <Button type="submit" disabled={!code.trim() || !name.trim() || update.isPending}>
              {update.isPending ? 'Salvo…' : 'Salva modifiche'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditFamilyDialog;
